import type { CampaignGroupType, CampaignStatus, CampaignType } from '~/types/graphql';

import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { getRoutesPathByCampaign } from '~/modules/campaign/helpers';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { useHandleNavigate } from '~/hooks/useHandleNavigate';
import { INavigateKey } from '~/types/navigate';
import { ROUTE_PATH } from '~/config/navigate';

export const useCampaignNavigate = () => {
  const { handleNavigate } = useHandleNavigate();
  const { handleOpenPageGemXInNewTab } = useLocationHandlers();

  const onEditCampaign = (
    campaign: { type?: CampaignType | CampaignGroupType; id: string },
    options?: { newTab?: boolean; replace?: boolean },
  ) => {
    const path = getRoutesPathByCampaign(campaign);
    if (options?.newTab) {
      handleOpenPageGemXInNewTab(path);
      return;
    }

    handleNavigate(path, options?.replace ? { setting: { replace: true } } : undefined);
  };

  const onAnalyticsCampaign = (
    {
      id,
      type,
    }: {
      id: string;
      type?: CampaignType | CampaignGroupType;
    },
    options?: { oldVersion?: boolean; params?: Record<string, string> },
  ) => {
    switch (type) {
      case CAMPAIGN_TYPE.TEMPLATE_BUNDLE:
        handleNavigate(`${ROUTE_PATH[INavigateKey.CampaignMultipage]}/${id}/analytics`, {
          params: options?.params,
        });
        break;
      default:
        handleNavigate(
          `${ROUTE_PATH[INavigateKey.CampaignTemplate]}${options ? (options.oldVersion ? '/v1' : '') : ''}/${id}/analytics`,
          { params: options?.params },
        );
    }
  };

  // Shared row/card click behaviour: DRAFT opens the editor, everything else opens
  // analytics. Kept here so desktop, mobile, and group call sites stay in lockstep when
  // the rule changes (e.g. a new status is added).
  const navigateToCampaign = (campaign: {
    id: string;
    type?: CampaignType | CampaignGroupType;
    status?: CampaignStatus | null;
  }) => {
    if (campaign.status === CAMPAIGN_STATUS.DRAFT) {
      onEditCampaign(campaign);
      return;
    }
    onAnalyticsCampaign(campaign);
  };

  return {
    onEditCampaign,
    onAnalyticsCampaign,
    navigateToCampaign,
  };
};
